<template>
  <div class="pdf-viewer">
    <!-- PDF 工具栏（原生 PDF.js 渲染） -->
    <div class="pdf-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <button @click="prevPage" :disabled="currentPage <= 1" class="toolbar-btn">上一页</button>
        <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
        <button @click="nextPage" :disabled="currentPage >= pageCount" class="toolbar-btn">下一页</button>
        <input
          type="number"
          v-model="pageInput"
          @keyup.enter="handlePageJump"
          class="page-input"
          min="1"
          placeholder="页码"
        />
        <button @click="handlePageJump" class="toolbar-btn">跳转</button>
      </div>
      <div class="toolbar-right">
        <div v-if="loading && _loadingProgress !== -2" class="loading-progress-bar">
          <!-- <span class="progress-text">{{ loadingProgressText }}</span> -->
          <div class="progress-track" :class="{ 'is-indeterminate': _loadingProgress < 0 }">
            <div
              class="progress-fill"
              :class="{ 'is-indeterminate': _loadingProgress < 0 }"
              :style="_loadingProgress >= 0 ? { width: _loadingProgress + '%' } : null"
            ></div>
          </div>
        </div>
        <button @click="downloadPdf" :disabled="!pdfUrl || downloading" class="toolbar-btn" :class="{ 'is-loading': downloading }">
          <span v-if="downloading">下载中...</span>
          <span v-else>下载</span>
        </button>
        <button @click="zoomOut" class="toolbar-btn">-</button>
        <span class="scale-info">{{ Math.round(scale * 100) }}%</span>
        <button @click="zoomIn" class="toolbar-btn">+</button>
        <button @click="toggleRotation" class="toolbar-btn" title="顺时针旋转90度">
          <i class="el-icon-refresh"></i>
        </button>
        <span class="rotation-info">{{ rotation }}°</span>
        <button v-if="showVerifySeal" type="primary" @click="$emit('verify-seal')" class="toolbar-btn verify-seal-btn" title="验章">
          验章
        </button>
      </div>
    </div>

    <!-- PDF 内容区域（原生 PDF.js 渲染） -->
    <div class="pdf-content" ref="pdfContent">
      <div v-if="loading" class="pdf-loading">
        <i class="el-icon-loading"></i>
        <p >PDF 加载中...</p>
      </div>
      <div v-else-if="pdfUrl" class="pdf-pages" ref="pdfPages">
        <div v-for="n in allPages" :key="n" class="pdf-page" :data-page="n" :style="pageStyle(n)">
          <div class="pdf-page-container" :style="containerStyle(n)">
            <canvas :ref="'pageCanvas_' + n" class="pdf-document"></canvas>
          </div>
        </div>
      </div>
      <div v-else class="pdf-placeholder">
        <i class="el-icon-document"></i>
        <p>请选择PDF文件查看</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdfViewer',
  props: {
    pdfUrl: {
      type: String,
      default: ''
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    downloadFileName: {
      type: String,
      default: 'document.pdf'
    },
    initialScale: {
      type: Number,
      default: 1.0
    },
    showVerifySeal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      pdfDocument: null,
      currentPage: 1,
      pageInput: '1',
      scale: 1.0,
      pdfjsLib: null,
      rotation: 0,
      isRenderingAll: false,
      _scrollTargets: [],
      _io: null,
      _renderedPages: new Set(),
      _renderingPages: new Set(),
      _renderQueue: [],
      _activeRenders: 0,
      _renderTasks: {},
      pageCount: 0,
      currentCdnHost: 'https://unpkg.zhimg.com/pdfjs-dist@2.16.105/',
      pageHeight: 800,
      pageGap: 12,
      virtualBuffer: 2,
      jumpRenderBuffer: 1,
      scrollCurrentPage: 1,
      _scrollRafId: null,
      _scrollTimerRafId: null,
      _scrollSettleTimer: null,
      _scrollSettleDelay: 100,
      _isUserScrollingFast: false,
      _scrollAdjustTs: 0,
      _pageObserver: null,
      _pageObserverPollRaf: null,
      pageRenderCompleted: {},
      pageLoadingStates: {},
      _loadingTotalBytes: 0,
      _loadingLoadedBytes: 0,
      _loadingProgress: 0,
      _loadingStartTime: 0,
      _lastProgressUpdate: 0,
      _lastProgressEmitTs: 0,
      _fetchPhase: 'idle', // 'cdn' | 'parsing' | 'fetching' | 'done'
      _loadingSpeedSample: [],
      _rafId: null,
      _loadingPhaseTimer: null,
      _loadingHint: '',
      // 渲染缓存（Canvas → ImageData，避免重算）
      _pageCache: {},
      _maxCacheSize: 10,
      _baseMaxCacheSize: 10,
      _baseVirtualBuffer: 2,
      _baseJumpRenderBuffer: 1,
      _fastJumpMode: false,
      _fastJumpTarget: null,
      _fastJumpRestoreTimer: null,
      _fastJumpScaleFactor: 0.75,
      _fastJumpMinScale: 0.5,
      _fastJumpDistanceThreshold: 8,
      _dpiMode: 0,
      _lowDpiScale: 0.4,
      _normalDpiScale: 0.8,
      _highDpiScale: 1.0,
      _pendingUpgradePage: null,
      _upgradeRafId: null
    }
  },
  computed: {
    allPages() {
      if (!this.pageCount) return [];
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    },
    loadingProgressText() {
      if (this._loadingHint) {
        return this._loadingHint;
      }
      if (this._loadingProgress >= 0) {
        return `加载中 ${this._loadingProgress}%`;
      }
      if (this._loadingLoadedBytes > 0) {
        return `加载中 ${(this._loadingLoadedBytes / 1024 / 1024).toFixed(1)}MB`;
      }
      return '加载中...';
    }
  },
  watch: {
    pdfUrl: {
      handler(newUrl) {
        if (newUrl) {
          this.loadPdfFile(newUrl);
        } else {
          this.resetPdf();
        }
      },
      immediate: true
    },
    initialScale: {
      handler(newScale) {
        this.scale = newScale;
        if (this.pdfDocument) {
          this.updatePdfDisplay();
        }
      }
    },
    currentPage(newPage) {
      this.pageInput = String(newPage || 1);
    },
    pageCount(val) {
      if (val > 0 && this.pdfDocument) {
        this.$nextTick(() => {
          this.startIntersectionObserver();
          this.renderNearby(this.currentPage || 1, this.virtualBuffer);
        });
      }
    }
  },
  mounted() {
    if (!Array.isArray(this._scrollTargets)) {
      this._scrollTargets = []
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.addEventListener('scroll', this.handleScroll, { passive: true });
      this._scrollTargets.push(container);
    }
    this.bindScrollableAncestors();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll, { passive: true });
  },
  methods: {
    adjustPerformanceProfile(pageCount) {
      const totalPages = Number(pageCount) || 0;

      this.virtualBuffer = this._baseVirtualBuffer;
      this.jumpRenderBuffer = this._baseJumpRenderBuffer;
      this._maxCacheSize = this._baseMaxCacheSize;

      if (totalPages >= 400) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 0;
        this._maxCacheSize = 4;
      } else if (totalPages >= 200) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 1;
        this._maxCacheSize = 6;
      } else if (totalPages >= 100) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 1;
        this._maxCacheSize = 8;
      }
    },

    startPhaseProgress(startPercent, endPercent, messages, interval = 700) {
      this.stopPhaseProgress();
      const labels = Array.isArray(messages) && messages.length ? messages : ['处理中...'];
      let index = 0;
      let current = startPercent;
      this._loadingHint = labels[0];
      this._emitLoadingProgress(current, this._loadingTotalBytes, labels[0]);

      this._loadingPhaseTimer = setInterval(() => {
        index = (index + 1) % labels.length;
        current = Math.min(current + 1, endPercent);
        this._loadingHint = labels[index];
        this._emitLoadingProgress(current, this._loadingTotalBytes, labels[index]);
        if (current >= endPercent) {
          current = endPercent;
        }
      }, interval);
    },

    stopPhaseProgress() {
      if (this._loadingPhaseTimer) {
        clearInterval(this._loadingPhaseTimer);
        this._loadingPhaseTimer = null;
      }
    },

    getCanvasForPage(pageNumber) {
      const ref = this.$refs['pageCanvas_' + pageNumber];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    bindScrollableAncestors() {
      let el = this.$el && this.$el.parentElement;
      const seen = new Set();
      while (el && el !== document.body && el !== document.documentElement) {
        if (seen.has(el)) break;
        seen.add(el);
        try {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY;
          const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
          if (isScrollable) {
            el.addEventListener('scroll', this.handleScroll, { passive: true });
            this._scrollTargets.push(el);
          }
        } catch (e) { }
        el = el.parentElement;
      }
    },
    loadScriptWithFallback(primaryUrl, fallbackUrl) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = primaryUrl;
        script.async = true;
        
        script.onload = () => {
          resolve(false);
        };
        
        script.onerror = () => {
          console.warn(`[PdfViewer] 主 CDN 节点加载失败: ${primaryUrl}，正在尝试备用 CDN 节点...`);
          try {
            document.head.removeChild(script);
          } catch (e) {}
          
          const fallbackScript = document.createElement('script');
          fallbackScript.src = fallbackUrl;
          fallbackScript.async = true;
          
          fallbackScript.onload = () => {
            resolve(true);
          };
          fallbackScript.onerror = () => {
            reject(new Error('所有 PDF.js 资源加载点都不可用'));
          };
          
          document.head.appendChild(fallbackScript);
        };
        
        document.head.appendChild(script);
      });
    },

    async loadPdfJs() {
      if (this.pdfjsLib) {
        return this.pdfjsLib;
      }

      const primaryHost = 'https://unpkg.zhimg.com/pdfjs-dist@2.16.105/';
      const fallbackHost = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/';

      try {
        const isFallbackActive = await this.loadScriptWithFallback(
          primaryHost + 'build/pdf.min.js',
          fallbackHost + 'build/pdf.min.js'
        );

        this.pdfjsLib = window.pdfjsLib;
        this.currentCdnHost = isFallbackActive ? fallbackHost : primaryHost;

        this.pdfjsLib.GlobalWorkerOptions.workerSrc = this.currentCdnHost + 'build/pdf.worker.min.js';
        return this.pdfjsLib;
      } catch (error) {
        console.error('PDF.js加载失败：', error);
        throw error;
      }
    },

    async loadPdfFile(src) {
      try {
        this.loading = true;
        this.currentPage = 1;
        this.scale = this.initialScale;

        this._loadingProgress = 0;
        this._loadingTotalBytes = 0;
        this._loadingLoadedBytes = 0;
        this._loadingStartTime = Date.now();
        this._lastProgressUpdate = 0;
        this._lastProgressEmitTs = 0;
        this._fetchPhase = 'cdn';
        this._loadingHint = '';
        this.stopPhaseProgress();
        this._loadingSpeedSample = [];
      this._pageCache = {};
      this._pendingUpgradePage = null;
      this._stopFastJump();
      if (this._upgradeRafId) {
          cancelAnimationFrame(this._upgradeRafId);
          this._upgradeRafId = null;
        }

        Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
        this._renderTasks = {};
        this._renderQueue = [];
        this._activeRenders = 0;
        this._renderedPages = new Set();
        this._renderingPages = new Set();
        this.pageRenderCompleted = {};
        this.pageLoadingStates = {};

        // ---- 阶段 1：加载 PDF.js 库 ----
        this._emitLoadingProgress(0, 0, '正在加载渲染引擎...');
        const pdfjsLib = await this.loadPdfJs();
        if (!pdfjsLib) throw new Error('PDF.js加载失败');
        this._fetchPhase = 'fetching';

        let source = null;
        const s = (src || '').trim();
        if (!s) throw new Error('无有效的PDF');

        const isDataUrl = s.startsWith('data:application/pdf;base64,');
        const isLikelyBase64 = !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s) && /^[A-Za-z0-9+/=\s]+$/.test(s);

        if (isDataUrl || isLikelyBase64) {
          source = {
            data: isDataUrl
              ? this.base64ToUint8Array(s.split(',')[1] || '')
              : this.base64ToUint8Array(s)
          };
        } else {
          source = {
            url: s,
            rangeChunkSize: 1024 * 1024,
            disableAutoFetch: true,
            disableStream: false,
            withCredentials: false,
            onProgress: (progressEvent) => {
              const loaded = progressEvent.loaded || 0;
              const total = progressEvent.total || 0;
              this._loadingLoadedBytes = loaded;
              this._loadingTotalBytes = total;

              if (total > 0 && loaded > 0) {
                const pct = Math.min(Math.round((loaded / total) * 100), 99);
                this._loadingProgress = pct;
                const now = Date.now();
                const elapsed = (now - this._loadingStartTime) / 1000;

                this._loadingSpeedSample.push({ ts: now, bytes: loaded });
                if (this._loadingSpeedSample.length > 10) this._loadingSpeedSample.shift();
                let speedKBps = 0;
                const sample = this._loadingSpeedSample;
                if (sample.length >= 2) {
                  const dt = (sample[sample.length - 1].ts - sample[0].ts) / 1000;
                  if (dt > 0) {
                    speedKBps = Math.round((sample[sample.length - 1].bytes - sample[0].bytes) / dt / 1024);
                  }
                }
                const remain = speedKBps > 0 ? Math.round((total - loaded) / 1024 / speedKBps) : 0;

                const shouldEmit = now - this._lastProgressEmitTs > 300 || pct >= 99;
                if (shouldEmit) {
                  this._lastProgressUpdate = pct;
                  this._lastProgressEmitTs = now;

                  console.log(`[PdfViewer] PDF 拉取: ${pct}%  (${(loaded / 1024 / 1024).toFixed(1)}MB / ${(total / 1024 / 1024).toFixed(1)}MB)  ${speedKBps}KB/s  剩余${remain}s`);
                  this._emitLoadingProgress(pct, total,
                    `正在下载 PDF (${pct}%)  ${speedKBps}KB/s  剩余${remain}s`);
                }

                // 超大/慢速文件自动降 DPI
                if ((total > 100 * 1024 * 1024 || (elapsed > 5 && pct < 80)) && this._dpiMode === 0) {
                  this._dpiMode = 1;
                  this.scale = this._lowDpiScale;
                  console.log('[PdfViewer] 超大文件，启用流畅模式 scale=' + this._lowDpiScale);
                }
              } else if (total === 0 && loaded > 0) {
                this._loadingProgress = -1;
                const now = Date.now();
                const loadedMB = (loaded / 1024 / 1024).toFixed(1);
                if (now - this._lastProgressEmitTs > 300) {
                  this._lastProgressEmitTs = now;
                  this._emitLoadingProgress(-1, 0, `正在下载 PDF（已接收 ${loadedMB}MB）`);
                }
              }
            }
          };
        }

        const loadingTask = pdfjsLib.getDocument(Object.assign({
          verbosity: 0,
          cMapUrl: this.currentCdnHost + 'cmaps/',
          cMapPacked: true
        }, source));

        // ---- 阶段 2：解析 PDF 结构 ----
        this._fetchPhase = 'parsing';
        this.startPhaseProgress(99, 99, ['正在解析 PDF 结构...', '正在读取页数信息...', '正在准备页面索引...'], 900);
        this.pdfDocument = await loadingTask.promise;
        this.stopPhaseProgress();
        this._fetchPhase = 'done';
        this._loadingHint = `解析完成，共 ${this.pdfDocument.numPages} 页`;
        this._loadingProgress = 100;
        this.pageCount = this.pdfDocument.numPages;
        this.adjustPerformanceProfile(this.pageCount);
        this.scrollCurrentPage = 1;
        this.currentPage = 1;
        this._renderedPages = new Set();
        this._renderingPages = new Set();

        this._emitLoadingProgress(100, 0, `解析完成，共 ${this.pageCount} 页`);

        // 获取第一页实际高度
        this._loadingHint = '正在准备第一页...';
        const firstPage = await this.pdfDocument.getPage(1);
        const vp = firstPage.getViewport({ scale: this.scale, rotation: this.rotation });
        this.pageHeight = vp.height;
        firstPage.cleanup();

        await this.$nextTick();
        await this.$nextTick();

        // 渲染首页（IntersectionObserver 会接管后续懒加载）
        this._loadingHint = '正在渲染首页...';
        this.renderPage(1);
        if (this.pageCount >= 2) {
          this.renderPage(2);
        }

        this.loading = false;
        const elapsed = ((Date.now() - this._loadingStartTime) / 1000).toFixed(1);
        console.log(`[PdfViewer] PDF 加载完成，总页数：${this.pageCount}，耗时：${elapsed}s，scale=${this.scale}`);
        this.$emit('pdf-loaded', {
          totalPages: this.pageCount,
          currentPage: this.currentPage,
          loadTimeSec: parseFloat(elapsed)
        });
      } catch (error) {
        this.stopPhaseProgress();
        this._loadingHint = '';
        console.error('[PdfViewer] PDF加载失败：', error);
        if (error.message && error.message.includes('Failed to fetch')) {
          this.$message.error('PDF加载失败: 跨域访问被阻止，请确保PDF文件支持CORS或使用同域文件');
        }
        this.loading = false;
        this._loadingProgress = -2;
        this._emitLoadingProgress(-2, 0, '加载失败: ' + (error.message || '未知错误'));
        this.$emit('pdf-error', error);
      }
    },

    _emitLoadingProgress(percent, total, hint) {
      this._loadingProgress = percent;
      this.$emit('loading-progress', {
        percent,
        total,
        hint,
        phase: this._fetchPhase
      });
    },

    base64ToUint8Array(base64) {
      const cleaned = base64.replace(/\s+/g, '');
      const binaryString = atob(cleaned);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    },

    resolveDownloadFileName(url) {
      if (this.downloadFileName && String(this.downloadFileName).trim()) {
        return String(this.downloadFileName).trim();
      }
      const fallbackName = 'document.pdf';
      try {
        const s = String(url || '').trim();
        if (!s || s.startsWith('data:application/pdf;base64,')) {
          return fallbackName;
        }
        if (/^[A-Za-z0-9+/=\s]+$/.test(s) && !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s)) {
          return fallbackName;
        }
        const pathname = new URL(s, window.location.origin).pathname || '';
        const lastSegment = pathname.split('/').filter(Boolean).pop() || fallbackName;
        return /\.pdf$/i.test(lastSegment) ? lastSegment : `${lastSegment}.pdf`;
      } catch (e) {
        return fallbackName;
      }
    },

    triggerDownload(href, fileName) {
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async downloadPdf() {
      const s = String(this.pdfUrl || '').trim();
      if (!s) {
        this.$message.error('暂无可下载的PDF');
        return;
      }
      const fileName = this.resolveDownloadFileName(s);
      const isDataUrl = s.startsWith('data:application/pdf;base64,');
      const isLikelyBase64 = !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s) && /^[A-Za-z0-9+/=\s]+$/.test(s);
      this.downloading = true;
      try {
        if (isDataUrl) {
          this.triggerDownload(s, fileName);
          return;
        }
        if (isLikelyBase64) {
          const bytes = this.base64ToUint8Array(s);
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          this.triggerDownload(blobUrl, fileName);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          return;
        }
        if (s.startsWith('blob:')) {
          this.triggerDownload(s, fileName);
          return;
        }
        if (/^https?:\/\//i.test(s)) {
          window.open(s, '_blank');
          return;
        }
        const response = await fetch(s);
        if (!response.ok) throw new Error('下载失败');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        this.triggerDownload(blobUrl, fileName);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        console.error('PDF下载失败：', error);
        this.$message.error('PDF下载失败');
      } finally {
        this.downloading = false;
      }
    },

    cancelRender(pageNumber) {
      if (this._renderQueue) {
        this._renderQueue = this._renderQueue.filter(x => x !== pageNumber);
      }
      const task = this._renderTasks && this._renderTasks[pageNumber];
      if (task) {
        try {
          task.cancel();
        } catch (_) {}
        delete this._renderTasks[pageNumber];
      }
      if (this._renderingPages) {
        this._renderingPages.delete(pageNumber);
      }
    },

    unrenderPage(pageNumber) {
      this.cancelRender(pageNumber);
      const canvas = this.getCanvasForPage(pageNumber);
      if (canvas) {
        const context = canvas.getContext('2d');
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }
        canvas.width = 0;
        canvas.height = 0;
      }
      if (this._renderedPages) {
        this._renderedPages.delete(pageNumber);
      }
      // ✅ 优化3：重置渲染完成标志
      if (this.pageRenderCompleted) {
        this.$set(this.pageRenderCompleted, pageNumber, false);
      }
      if (this.pageLoadingStates) {
        this.$set(this.pageLoadingStates, pageNumber, false);
      }
    },

    renderPage(pageNumber) {
      if (!this.pdfDocument) return;
      if (!this._renderedPages) this._renderedPages = new Set();
      if (!this._renderingPages) this._renderingPages = new Set();
      if (!this._renderQueue) this._renderQueue = [];

      if (this._renderedPages.has(pageNumber) || this._renderingPages.has(pageNumber)) return;
      if (this._renderQueue.includes(pageNumber)) return;

      if (this.pageLoadingStates) {
        this.$set(this.pageLoadingStates, pageNumber, true);
      }

      this._renderQueue.push(pageNumber);
      this.processRenderQueue();
    },

    renderPageWithRetry(pageNumber, maxRetries = 5, delay = 100) {
      const attemptRender = (retryCount) => {
        const canvas = this.getCanvasForPage(pageNumber);
        if (canvas && canvas.width > 0) return;
        if (this._renderedPages && this._renderedPages.has(pageNumber)) return;
        
        this.renderPage(pageNumber);
        
        if (retryCount < maxRetries) {
          setTimeout(() => attemptRender(retryCount + 1), delay);
        }
      };
      
      this.$nextTick(() => attemptRender(0));
    },

    processRenderQueue() {
      const MAX_CONCURRENT = 2;
      if (!this._activeRenders) this._activeRenders = 0;
      if (!this._renderQueue) this._renderQueue = [];

      while (this._activeRenders < MAX_CONCURRENT && this._renderQueue.length > 0) {
        const pageNumber = this._renderQueue.shift();
        if (this._renderedPages.has(pageNumber) || this._renderingPages.has(pageNumber)) continue;

        this._activeRenders++;
        this._renderPageInternal(pageNumber).finally(() => {
          this._activeRenders--;
          this.processRenderQueue();
        });
      }
    },

    _startFastJump(pageNumber) {
      this._fastJumpMode = true;
      this._fastJumpTarget = pageNumber;
      if (this._fastJumpRestoreTimer) {
        clearTimeout(this._fastJumpRestoreTimer);
      }
      this._fastJumpRestoreTimer = setTimeout(() => {
        this._stopFastJump();
      }, 1200);
    },

    _stopFastJump() {
      if (this._fastJumpRestoreTimer) {
        clearTimeout(this._fastJumpRestoreTimer);
        this._fastJumpRestoreTimer = null;
      }
      this._fastJumpMode = false;
      this._fastJumpTarget = null;
    },

    getRenderScale(pageNumber) {
      if (this._fastJumpMode && this._fastJumpTarget === pageNumber) {
        return Math.max(this.scale * this._fastJumpScaleFactor, this._fastJumpMinScale);
      }
      if (this._dpiMode === 1) {
        return this._lowDpiScale;
      }
      return this.scale;
    },

    shouldCachePage(pageNumber) {
      if (!this._fastJumpMode) return true;
      return Math.abs(pageNumber - (this._fastJumpTarget || pageNumber)) <= 1;
    },

    async _renderPageInternal(pageNumber) {
      if (!this.pdfDocument) return;

      if (!this._renderingPages) this._renderingPages = new Set();
      this._renderingPages.add(pageNumber);

      const renderScale = this.getRenderScale(pageNumber);
      const isLowDpiMode = renderScale < this.scale;

      try {
        const page = await this.pdfDocument.getPage(pageNumber);
        let canvas = this.getCanvasForPage(pageNumber);

        if (!canvas) {
          for (let retry = 0; retry < 30; retry++) {
            await new Promise(resolve => setTimeout(resolve, 30));
            await this.$nextTick();
            canvas = this.getCanvasForPage(pageNumber);
            if (canvas) break;
          }
        }

        if (!canvas) {
          console.warn('[PdfViewer] canvas 未挂载，放弃渲染 page', pageNumber);
          page.cleanup();
          return;
        }

        const context = canvas.getContext('2d');
        const viewport = page.getViewport({
          scale: renderScale,
          rotation: this.rotation
        });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        if (pageNumber === 1) {
          this.pageHeight = viewport.height;
        }

        // ---- 核心优化：优先从缓存 ImageData 绘制 ----
        const cacheKey = `${pageNumber}_${renderScale}_${this.rotation}`;
        const cached = this._pageCache[cacheKey];

        if (cached && cached.width === canvas.width && cached.height === canvas.height) {
          try {
            context.putImageData(cached, 0, 0);
            if (this._renderedPages) this._renderedPages.add(pageNumber);
            this.$set(this.pageRenderCompleted, pageNumber, true);
            this.$set(this.pageLoadingStates, pageNumber, false);
            page.cleanup();
            if (this._renderingPages) this._renderingPages.delete(pageNumber);

            // 低 DPI → 高 DPI 自动升级
            if (isLowDpiMode && this._dpiMode !== 0) {
              this._scheduleDpiUpgrade(pageNumber);
            }
            return;
          } catch (e) {
            delete this._pageCache[cacheKey];
          }
        }

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        const renderTask = page.render(renderContext);
        if (!this._renderTasks) this._renderTasks = {};
        this._renderTasks[pageNumber] = renderTask;

        await renderTask.promise;
        delete this._renderTasks[pageNumber];

        // ---- 保存 ImageData 到缓存（限制缓存数量防止爆内存）----
        try {
          if (this.shouldCachePage(pageNumber)) {
            const imgData = context.getImageData(0, 0, canvas.width, canvas.height);
            const keys = Object.keys(this._pageCache);
            if (keys.length >= this._maxCacheSize) {
              delete this._pageCache[keys[0]];
            }
            this._pageCache[cacheKey] = imgData;
          }
        } catch (e) {
          // getImageData 可能因 canvas 跨域污染而抛错，忽略即可
        }

        if (this._renderedPages) {
          this._renderedPages.add(pageNumber);
        }

        this.$emit('page-rendered', {
          pageNumber: pageNumber,
          totalPages: this.pageCount
        });
        page.cleanup();

        // 低 DPI → 高 DPI 自动升级
        if (isLowDpiMode && this._dpiMode !== 0) {
          this._scheduleDpiUpgrade(pageNumber);
        }
      } catch (error) {
        if (error.name !== 'RenderingCancelledException') {
          console.warn('渲染页面出错:', pageNumber, error);
          this.$emit('render-error', error);
        }
      } finally {
        if (this._renderingPages) {
          this._renderingPages.delete(pageNumber);
        }
        if (this.pageLoadingStates) {
          this.$set(this.pageLoadingStates, pageNumber, false);
        }
        if (this.pageRenderCompleted) {
          this.$set(this.pageRenderCompleted, pageNumber, true);
        }
      }
    },


    _scheduleDpiUpgrade(pageNumber) {
      if (this._pendingUpgradePage === pageNumber) return;
      this._pendingUpgradePage = pageNumber;

      if (this._upgradeRafId) cancelAnimationFrame(this._upgradeRafId);
      this._upgradeRafId = requestAnimationFrame(() => {
        this._upgradeRafId = null;
        if (this._pendingUpgradePage !== pageNumber) return;
        this._pendingUpgradePage = null;

        if (this._dpiMode === 1) {
          this._dpiMode = 2;
          this.scale = this._normalDpiScale;
          console.log('[PdfViewer] 升级到标准模式 (scale=' + this._normalDpiScale + ')');
          this.$emit('dpi-mode-changed', { mode: 2, scale: this._normalDpiScale });
          this.$nextTick(() => this.updatePdfDisplay());
        } else if (this._dpiMode === 2) {
          this._dpiMode = 3;
          this.scale = this._highDpiScale;
          console.log('[PdfViewer] 升级到高清模式 (scale=' + this._highDpiScale + ')');
          this.$emit('dpi-mode-changed', { mode: 3, scale: this._highDpiScale });
          this.$nextTick(() => this.updatePdfDisplay());
        }
      });
    },

    renderNearby(centerPage, radius = 2) {
      if (!this.pdfDocument || !this.pageCount) return;
      const start = Math.max(1, centerPage - radius);
      const end = Math.min(this.pageCount, centerPage + radius);
      for (let i = start; i <= end; i++) {
        this.renderPage(i);
      }
    },

    pauseIntersectionObserver() {
      if (this._pageObserver) {
        this._pageObserver.disconnect();
        this._pageObserver = null;
      }
    },

    resumeIntersectionObserver() {
      if (!this.pdfDocument || this._pageObserver || this._isUserScrollingFast) return;
      this.startIntersectionObserver();
    },

    startIntersectionObserver() {
      if (!this.pdfDocument || this._isUserScrollingFast) return;

      // 销毁旧的
      this.pauseIntersectionObserver();
      if (this._pageObserverPollRaf) {
        cancelAnimationFrame(this._pageObserverPollRaf);
        this._pageObserverPollRaf = null;
      }

      this.$nextTick(() => {
        const container = this.$refs.pdfContent;
        if (!container) return;

        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) return;
              const pageNum = Number(entry.target.dataset.page);
              if (!pageNum || isNaN(pageNum)) return;
              this.currentPage = pageNum;
              this.scrollCurrentPage = pageNum;
              this.renderNearby(pageNum, this.virtualBuffer);
            });
          },
          { root: container, rootMargin: '200px', threshold: 0 }
        );

        this._pageObserver = io;

        container.querySelectorAll('.pdf-page').forEach(el => {
          io.observe(el);
        });
      });
    },

    async updatePdfDisplay() {
      if (!this.pdfDocument || this.isRenderingAll) return;
      this.isRenderingAll = true;
      try {
        Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
        this._renderTasks = {};
        this._renderQueue = [];
        this._activeRenders = 0;

        this._renderedPages = new Set();
        this._renderingPages = new Set();
        this.pageRenderCompleted = {};
        this.pageLoadingStates = {};

        const firstPage = await this.pdfDocument.getPage(1);
        const vp = firstPage.getViewport({ scale: this.scale, rotation: this.rotation });
        this.pageHeight = vp.height;
        firstPage.cleanup();

        this.scrollCurrentPage = this.currentPage || 1;
        await this.$nextTick();
        this.startIntersectionObserver();
        this.renderNearby(this.scrollCurrentPage, this.virtualBuffer);
      } finally {
        this.isRenderingAll = false;
      }
    },


    async prevPage() {
      if (this.currentPage > 1) {
        this.scrollToPage(this.currentPage - 1);
      }
    },

    async nextPage() {
      if (this.currentPage < this.pageCount) {
        this.scrollToPage(this.currentPage + 1);
      }
    },

    async zoomIn() {
      this.scale = Math.min(this.scale + 0.25, 3.0);
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async zoomOut() {
      this.scale = Math.max(this.scale - 0.25, 0.2);
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async fitWidth() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async fitPage() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async goToPage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.pageCount) {
        this.scrollToPage(pageNumber);
      }
    },

    handlePageJump() {
      const pageNumber = Number(this.pageInput);
      if (!Number.isInteger(pageNumber)) {
        this.$message.warning('请输入正确的页码');
        this.pageInput = String(this.currentPage || 1);
        return;
      }
      if (pageNumber < 1 || pageNumber > this.pageCount) {
        this.$message.warning(`请输入 1 到 ${this.pageCount} 之间的页码`);
        this.pageInput = String(this.currentPage || 1);
        return;
      }
      this.goToPage(pageNumber);
    },

    resetPdf() {
      this.loading = false;
      this.pdfDocument = null;
      this.currentPage = 1;
      this.pageCount = 0;
      this.pageInput = '1';
      this.scale = this.initialScale;
      this.virtualBuffer = this._baseVirtualBuffer;
      this.jumpRenderBuffer = this._baseJumpRenderBuffer;
      this._maxCacheSize = this._baseMaxCacheSize;
      this.scrollCurrentPage = 1;
      this._loadingProgress = 0;
      this._loadingTotalBytes = 0;
      this._loadingLoadedBytes = 0;
      this._loadingHint = '';
      this.stopPhaseProgress();
      this._pageCache = {};
      this._pendingUpgradePage = null;
      this._stopFastJump();
      if (this._upgradeRafId) {
        cancelAnimationFrame(this._upgradeRafId);
        this._upgradeRafId = null;
      }
      Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
      this._renderTasks = {};
      this._renderQueue = [];
      this._activeRenders = 0;
      this._renderedPages = new Set();
      this._renderingPages = new Set();
      this.pageRenderCompleted = {};
      this.pageLoadingStates = {};
    },

    toggleRotation() {
      this.rotation = (this.rotation + 90) % 360;
      if (this.pdfDocument) {
        this.updatePdfDisplay();
      }
    },

    getPdfInfo() {
      return {
        currentPage: this.currentPage,
        totalPages: this.pageCount,
        scale: this.scale,
        rotation: this.rotation,
        loading: this.loading,
        hasPdf: !!this.pdfDocument
      };
    },

    pageStyle(pageNumber) {
      const h = this.pageHeight || 1100;
      return {
        minHeight: h + 'px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: (this.pageGap || 12) + 'px'
      };
    },

    containerStyle(pageNumber) {
      const h = this.pageHeight || 1100;
      const w = Math.round(h / 1.4142);
      return {
        width: w ? (w + 'px') : '800px',
        height: h + 'px',
        position: 'relative'
      };
    },

    scrollToPage(pageNumber) {
      if (!pageNumber || pageNumber < 1 || pageNumber > this.pageCount) return;

      const prevPage = this.currentPage || 1;
      const jumpDistance = Math.abs(pageNumber - prevPage);
      const isLongJump = jumpDistance > this._fastJumpDistanceThreshold;

      if (isLongJump) {
        this._startFastJump(pageNumber);
      } else {
        this._stopFastJump();
      }

      this.currentPage = pageNumber;
      this.scrollCurrentPage = pageNumber;

      this.$nextTick(() => {
        const container = this.$refs.pdfContent;
        const targetOffset = (pageNumber - 1) * (this.pageHeight + this.pageGap);

        if (container) {
          if (isLongJump) {
            container.scrollTop = targetOffset;
          } else {
            const targetPageEl = this.$el.querySelector(`.pdf-page[data-page="${pageNumber}"]`);
            if (targetPageEl) {
              targetPageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              container.scrollTop = targetOffset;
            }
          }
        }

        const renderRadius = isLongJump ? this.jumpRenderBuffer : this.virtualBuffer;
        this.renderPage(pageNumber);
        this.renderNearby(pageNumber, renderRadius);
        this.handleScroll();

        if (isLongJump) {
          const settlePage = pageNumber;
          setTimeout(() => {
            if (this.currentPage === settlePage) {
              this._stopFastJump();
              this.renderNearby(settlePage, this.virtualBuffer);
            }
          }, 260);
        }
      });
    },

    // 使用 RAF 优化滚动性能，防止重复触发
    handleScroll() {
      if (!this._isUserScrollingFast) {
        this.pauseIntersectionObserver();
      }
      this._isUserScrollingFast = true;
      if (this._scrollSettleTimer) {
        clearTimeout(this._scrollSettleTimer);
      }
      this._scrollSettleTimer = setTimeout(() => {
        this._isUserScrollingFast = false;
        this._scrollSettleTimer = null;
        this._doHandleScroll(true);
        this.resumeIntersectionObserver();
      }, this._scrollSettleDelay);

      if (this._scrollTimerRafId) return;
      this._scrollTimerRafId = requestAnimationFrame(() => {
        this._scrollTimerRafId = null;
        this._doHandleScroll(false);
      });
    },

    _doHandleScroll(shouldRenderNearby = false) {
      const container = this.$refs.pdfContent;
      if (!container || !this.pageCount) return;

      const scrollTop = container.scrollTop;
      const pageFullHeight = this.pageHeight + this.pageGap;
      let newPage = Math.floor(scrollTop / pageFullHeight) + 1;
      newPage = Math.max(1, Math.min(newPage, this.pageCount));

      const prevCurrent = this.currentPage;
      this.currentPage = newPage;
      this.scrollCurrentPage = newPage;

      if (shouldRenderNearby || !this._isUserScrollingFast) {
        const renderRadius = this._fastJumpMode ? this.jumpRenderBuffer : this.virtualBuffer;
        this.renderPage(newPage);
        this.renderNearby(newPage, renderRadius);
      }

      if (newPage !== prevCurrent) {
        this.$emit('page-changed', { currentPage: newPage, totalPages: this.pageCount });
      }
    }
  },
  beforeDestroy() {
    this.stopPhaseProgress();
    if (this._scrollRafId) {
      cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }
    if (this._scrollTimerRafId) {
      cancelAnimationFrame(this._scrollTimerRafId);
      this._scrollTimerRafId = null;
    }
    if (this._scrollSettleTimer) {
      clearTimeout(this._scrollSettleTimer);
      this._scrollSettleTimer = null;
    }
    if (this._pageObserverPollRaf) {
      cancelAnimationFrame(this._pageObserverPollRaf);
      this._pageObserverPollRaf = null;
    }
    if (this._pageObserver) {
      this._pageObserver.disconnect();
      this._pageObserver = null;
    }
    if (this._upgradeRafId) {
      cancelAnimationFrame(this._upgradeRafId);
      this._upgradeRafId = null;
    }
    this._stopFastJump();
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
    }
    if (Array.isArray(this._scrollTargets)) {
      this._scrollTargets.forEach(t => {
        try { t.removeEventListener('scroll', this.handleScroll); } catch (e) { }
      });
      this._scrollTargets = [];
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
    if (this._io) {
      try { this._io.disconnect(); } catch (e) { }
      this._io = null;
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  font-size: 13px;
  color: #333;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.toolbar-btn:hover:not(:disabled) {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.toolbar-btn:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
  opacity: 0.6;
}

.toolbar-btn.is-loading {
  color: #909399;
}

.verify-seal-btn {
  color: #fff;
  background: #409eff;
  border-color: #409eff;
}

.verify-seal-btn:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.page-input {
  width: 72px;
  padding: 4px 8px;
  font-size: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  outline: none;
  text-align: center;
}

.page-input:focus {
  border-color: #409eff;
}

.loading-progress-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #409eff;
  white-space: nowrap;
}

.progress-text {
  min-width: 70px;
  font-weight: 500;
}

.progress-track {
  width: 120px;
  height: 6px;
  background: #e4e7ed;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.progress-track.is-indeterminate {
  background: #eef4ff;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #66b1ff, #409eff);
  border-radius: 3px;
  transition: width 0.3s ease;
  max-width: 100%;
}

.progress-fill.is-indeterminate {
  position: absolute;
  top: 0;
  left: 0;
  width: 36px;
  max-width: none;
  animation: progress-indeterminate 1.1s ease-in-out infinite;
}

@keyframes progress-indeterminate {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(420%);
  }
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-info,
.scale-info,
.rotation-info {
  font-size: 14px;
  color: #666;
  min-width: 60px;
  text-align: center;
}

.pdf-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  background: #f9f9f9;
}

.pdf-pages {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.pdf-page {
  display: flex;
  justify-content: center;
}

.pdf-loading {
  text-align: center;
  color: #666;
}

.pdf-loading i {
  font-size: 24px;
  margin-bottom: 10px;
}

.pdf-placeholder {
  text-align: center;
  color: #999;
}

.pdf-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.pdf-document {
  max-width: 100%;
  background: white;
  display: block;
}

.pdf-page-container {
  position: relative;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.pdf-page-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.94);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.mask-content {
  text-align: center;
  color: #409EFF;
}

.mask-content i {
  font-size: 32px;
  margin-bottom: 10px;
}

.mask-content p {
  font-size: 13px;
  margin: 0;
  color: #8c939d;
  font-weight: 500;
}

@media (max-width: 768px) {
  .pdf-toolbar {
    justify-content: center;
  }
}
</style>
